import { RequestRegistrationStatus, Role } from '@customTypes/types';
import { LinkIcon } from '@heroicons/react/24/outline';
import {
  Alert,
  AppShell,
  Button,
  Card,
  Center,
  Flex,
  Loader,
  Modal,
  ScrollArea,
  Select,
  Table,
  Text,
  TextInput,
  Title
} from '@mantine/core';
import { useClipboard, useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';

import { useGetRegistrationRequestListQuery } from '@/features/admin-panel/api/getRegistrationRequestList/hook';
import { notificationStyleConfig } from '@/features/notification/config/notificationConfig';
import { useGenerateLoyaltyTokenMutation } from '@/shared/api/requests';
import { useApproveRegisterRequestMutation } from '@/shared/api/requests/loyalty/approve';
import { useDeleteRegisterRequestMutation } from '@/shared/api/requests/loyalty/delete';
import { useRejectRegisterRequestMutation } from '@/shared/api/requests/loyalty/reject';
import { translate } from '@/shared/i18n/translate';
import { LBButton, LBTextInput } from '@/shared/ui-kit/components';

export const RegisterRequests = () => {
  /* ──────────────────────────────
   * Hooks & state
   * ────────────────────────────── */
  const queryClient = useQueryClient();
  const clipboard = useClipboard();
  const { data, isLoading, isError } = useGetRegistrationRequestListQuery();

  const approveMutation = useApproveRegisterRequestMutation();
  const rejectMutation = useRejectRegisterRequestMutation();
  const deleteMutation = useDeleteRegisterRequestMutation();

  /* ──────────────────────────────
   * Loyalty token generation
   * ────────────────────────────── */
  const [selectedRole, setSelectedRole] = useState<Role>(Role.artist);
  const [loyaltyToken, setLoyaltyToken] = useState<string>('');
  const loyalty = useGenerateLoyaltyTokenMutation();
  const isGenerated = useRef(false);

  const fetchToken = async (role: Role) => {
    const response = await loyalty.mutateAsync({ params: { role } });
    setLoyaltyToken(response.data.token);
  };

  /* First load */
  useEffect(() => {
    if (!isGenerated.current) {
      fetchToken(selectedRole);
      isGenerated.current = true;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* Refresh token when role changes */
  useEffect(() => {
    if (isGenerated.current) {
      fetchToken(selectedRole);
    }
  }, [selectedRole]);

  const onCopy = async () => {
    clipboard.copy(`${import.meta.env.VITE_APP_URL}/invite/${loyaltyToken}`);
    /* Immediately issue a fresh token so the copied link is single-use */
    await fetchToken(selectedRole);
  };

  useEffect(() => {
    if (clipboard.copied) {
      notifications.show({
        title: translate('notifications.linkIsCopied'),
        ...notificationStyleConfig
      });
    }
  }, [clipboard.copied]);

  /* ──────────────────────────────
   * Mutations helpers
   * ────────────────────────────── */
  const invalidateList = () =>
    queryClient.invalidateQueries({ queryKey: ['GetRegistrationRequestList'] });

  const approve = async (requestId: string) => {
    await approveMutation.mutateAsync({ params: { requestId } });
    invalidateList();
  };

  const reject = async (requestId: string) => {
    await rejectMutation.mutateAsync({ params: { requestId } });
    invalidateList();
  };

  /* ──────────────────────────────
   * Delete confirmation modal
   * ────────────────────────────── */
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [confirmInput, setConfirmInput] = useState('');
  const [isDeleteOpen, { open: openDelete, close: closeDelete }] =
    useDisclosure();

  const onDeleteClick = (id: string) => {
    setDeleteId(id);
    setConfirmInput('');
    openDelete();
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    await deleteMutation.mutateAsync({ params: { requestId: deleteId } });
    closeDelete();
    invalidateList();
  };

  /* ──────────────────────────────
   * Table helpers
   * ────────────────────────────── */
  const statusLabels: Record<RequestRegistrationStatus, string> = {
    [RequestRegistrationStatus.Pending]: 'Ожидание',
    [RequestRegistrationStatus.Approved]: 'Одобрено',
    [RequestRegistrationStatus.Rejected]: 'Отклонено'
  };

  const rows = data?.data.map((req) => {
    const hasClubName =
      req.clubName !== null && req.clubName?.toString().trim() !== '';

    return (
      <Table.Tr
        key={req.id}
        /* highlight requests with club name */
        style={
          hasClubName
            ? {
                borderLeft: `6px solid var(--mantine-color-red-6)`,
                backgroundColor: 'var(--mantine-color-yellow-2)'
              }
            : undefined
        }
      >
        <Table.Td>{req.id}</Table.Td>
        {/* Date */}
        <Table.Td>{dayjs(req.createdAt).format('DD.MM.YYYY HH:mm')}</Table.Td>
        {/* Name, club, phone, email, source */}
        <Table.Td>{req.name}</Table.Td>
        <Table.Td>{req.clubName ?? '-'}</Table.Td>
        <Table.Td>{req.phoneNumber}</Table.Td>
        <Table.Td>{req.email}</Table.Td>
        <Table.Td>{req.howDidYouHearAboutUs}</Table.Td>
        {/* Status (moved next to actions) */}
        <Table.Td>
          <Text
            fw={500}
            fz={14}
            c={
              req.status === RequestRegistrationStatus.Approved
                ? 'green'
                : req.status === RequestRegistrationStatus.Pending
                  ? 'yellow'
                  : 'red'
            }
          >
            {statusLabels[req.status]}
          </Text>
        </Table.Td>
        {/* Actions */}
        <Table.Td>
          <Flex direction='column' gap='xs' justify='center'>
            {req.status === RequestRegistrationStatus.Pending && (
              <>
                <Button
                  color='green'
                  size='xs'
                  variant='outline'
                  onClick={() => approve(req.id.toString())}
                >
                  Одобрить
                </Button>
                <Button
                  color='yellow'
                  size='xs'
                  variant='outline'
                  onClick={() => reject(req.id.toString())}
                >
                  Отклонить
                </Button>
              </>
            )}
            <Button
              color='red'
              size='xs'
              variant='outline'
              onClick={() => onDeleteClick(req.id.toString())}
            >
              Удалить
            </Button>
          </Flex>
        </Table.Td>
      </Table.Tr>
    );
  });

  /* ──────────────────────────────
   * Render
   * ────────────────────────────── */
  return (
    <AppShell.Main>
      {/* Delete confirmation modal */}
      <Modal
        withCloseButton
        opened={isDeleteOpen}
        overlayProps={{ blur: 4, opacity: 0.6 }}
        title='Удаление запроса'
        onClose={closeDelete}
      >
        <Text mb='sm'>
          Вы уверены, что хотите удалить запрос? Действие необратимо. Введите
          «delete» для подтверждения.
        </Text>
        <TextInput
          placeholder='Введите delete для подтверждения'
          value={confirmInput}
          onChange={(e) => setConfirmInput(e.currentTarget.value)}
        />
        <Flex gap='xs' justify='end' mt='md'>
          <Button variant='default' onClick={closeDelete}>
            Отмена
          </Button>
          <Button
            color='red'
            disabled={confirmInput !== 'delete'}
            onClick={confirmDelete}
          >
            Удалить
          </Button>
        </Flex>
      </Modal>

      {/* Header */}
      <Flex align='center' justify='space-between' mb='lg'>
        <Title order={2}>Запросы на регистрацию</Title>
      </Flex>

      {/* Invite generator */}
      <Card withBorder mb='lg' p='lg' radius='md' shadow='sm'>
        <Flex align='center' gap='xs' wrap='wrap'>
          <Select
            label='Кого приглашаем'
            value={selectedRole}
            w={240}
            data={[
              { value: 'Artist', label: 'Артистку' },
              { value: 'ClubOwner', label: 'Клуб' }
            ]}
            onChange={(val) => setSelectedRole(val as Role)}
          />
          <LBTextInput
            readOnly
            value={`${import.meta.env.VITE_APP_URL}/invite/${loyaltyToken}`}
            w='100%'
          />
          <LBButton leftSection={<LinkIcon width={14} />} onClick={onCopy}>
            Скопировать ссылку
          </LBButton>
        </Flex>
      </Card>

      {/* Requests list */}
      {isLoading && (
        <Center style={{ height: 200 }}>
          <Loader />
        </Center>
      )}
      {isError && (
        <Alert color='red' mb='md' title='Ошибка'>
          Не удалось загрузить список запросов.
        </Alert>
      )}
      {!isLoading && data && (
        <Card withBorder p='lg' radius='md' shadow='sm'>
          <ScrollArea>
            <Table highlightOnHover striped withColumnBorders withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>ID</Table.Th>
                  <Table.Th>Дата</Table.Th>
                  <Table.Th>Имя</Table.Th>
                  <Table.Th>Клуб</Table.Th>
                  <Table.Th>Телефон</Table.Th>
                  <Table.Th>Email</Table.Th>
                  <Table.Th>Как узнали</Table.Th>
                  <Table.Th>Статус</Table.Th>
                  <Table.Th />
                  {/* Actions */}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>{rows}</Table.Tbody>
            </Table>
          </ScrollArea>
        </Card>
      )}
    </AppShell.Main>
  );
};
